/**
 * Analyse PDF (préflight / contrôle prépresse) — modale.
 *
 * Charge 1+ PDF, sélectionne le(s) profil(s) ICC pour le TAC, lance l'analyse
 * (POST /api/export/preflight) et affiche un rapport structuré + badges de
 * sévérité. Téléchargement du rapport au format Markdown (.md).
 */
import { useEffect, useRef, useState } from 'react';
import { API_URL, getAuthHeader } from '../api/client';

const SEV = {
  error: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: '✕', label: 'Erreur' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: '!', label: 'Attention' },
  ok: { color: '#10b981', bg: 'rgba(16,185,129,0.12)', icon: '✓', label: 'OK' },
  info: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', icon: 'i', label: 'Info' },
};
const SEV_MD = { error: '❌', warning: '⚠️', ok: '✅', info: 'ℹ️' };
const FONT_SCALES = [0.9, 1, 1.15, 1.35]; // paliers du bouton « taille du texte »

/**
 * fetch authentifié qui RAFRAÎCHIT le JWT sur 401 puis réessaie (comme apiFetch /
 * aiFetch). Sans ça, la modale tombait en 401 dès que le token d'accès expirait.
 * Retourne la Response brute (l'appelant gère .ok / .json / detail). Gère FormData
 * (aucun Content-Type forcé).
 */
async function authedFetch(path, options = {}) {
  const url = `${API_URL}${path}`;
  const headers = { ...(options.headers || {}), Authorization: getAuthHeader() };
  let res = await fetch(url, { ...options, headers });
  if (res.status === 401 && headers.Authorization?.startsWith('Bearer ')) {
    try {
      const { useAuthStore } = await import('../store/useAuthStore');
      const newToken = await useAuthStore.getState().refreshAccessToken();
      if (newToken) {
        headers.Authorization = 'Bearer ' + newToken;
        res = await fetch(url, { ...options, headers });
      }
    } catch { /* refresh échoué → l'appelant gère le 401 */ }
  }
  return res;
}

async function detailDErreur(r) {
  try { const j = await r.json(); if (j?.detail) return `Erreur ${r.status} : ${j.detail}`; } catch { /* corps non-JSON */ }
  return `Erreur ${r.status}`;
}

/**
 * Dépose un PDF sur S3 par URL présignée (la même voie que l'import) et renvoie
 * sa clé. Cloud Run refuse toute requête de plus de 32 Mio, et son 413 arrive
 * SANS en-tête CORS : le navigateur ne montre alors que « Failed to fetch ».
 * XHR plutôt que fetch : lui seul rapporte l'avancement de l'envoi.
 */
async function deposerSurS3(file, onProgress) {
  const form = new FormData();
  form.append('filename', file.name);
  form.append('size', String(file.size));
  const r = await authedFetch('/api/pdf/upload-url', { method: 'POST', body: form });
  if (!r.ok) throw new Error(await detailDErreur(r));
  const { upload_url, s3_key } = await r.json();
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload_url, true);
    xhr.setRequestHeader('Content-Type', 'application/pdf');
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) onProgress?.(ev.loaded / ev.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
      ? resolve()
      : reject(new Error(`L'envoi de « ${file.name} » a échoué (erreur ${xhr.status}). Relancez l'analyse dans un instant.`));
    xhr.onerror = () => reject(new Error(`L'envoi de « ${file.name} » a été interrompu. Vérifiez votre connexion, puis relancez l'analyse.`));
    xhr.ontimeout = () => reject(new Error(`L'envoi de « ${file.name} » a expiré : connexion trop lente pour ce poids.`));
    xhr.timeout = 30 * 60 * 1000;
    xhr.send(file);
  });
  return s3_key;
}

/**
 * Par où ce fichier rejoint-il le serveur ?
 *   'direct' → dans le corps de la requête d'analyse (multipart) ;
 *   's3'     → déposé d'abord sur S3, le serveur ne reçoit que la clé.
 * Le plafond dur est celui de Cloud Run : 32 Mio pour la requête ENTIÈRE,
 * enveloppe multipart et champ `profiles` compris. On bascule à 30 Mo, comme
 * l'import (MAX_PDF_MB dans EditorView) : deux Mio de marge, un seul seuil
 * dans toute l'application.
 */
const ENVOI_DIRECT_MAX = 30 * 1024 * 1024;
function voieDEnvoi(file) {
  return file.size > ENVOI_DIRECT_MAX ? 's3' : 'direct';
}

function buildMarkdown(reports) {
  const lines = [];
  const now = new Date();
  lines.push(`# Rapport d'analyse PDF (préflight)`);
  lines.push('');
  reports.forEach((r, idx) => {
    if (idx > 0) lines.push('\n---\n');
    lines.push(`## ${r.filename || 'Document'}`);
    if (r.error) { lines.push(`\n> ⚠️ ${r.error}`); return; }
    const s = r.summary || {};
    lines.push(`\n**${s.errors || 0} erreur(s) · ${s.warnings || 0} avertissement(s)**`);
    lines.push(`\n### Contrôles`);
    (r.checks || []).forEach((c) => lines.push(`- ${SEV_MD[c.severity] || ''} **${c.label}** : ${c.message}`));
    const d = r.document || {};
    lines.push(`\n### Document`);
    lines.push(`- Version : ${d.pdf_version || '?'}`);
    lines.push(`- Pages : ${d.page_count ?? '?'}`);
    if (d.producer) lines.push(`- Producteur : ${d.producer}`);
    if (d.title) lines.push(`- Titre : ${d.title}`);
    lines.push(`- Poids : ${Math.round((r.file_size || 0) / 1024)} Ko`);
    if ((r.pages || []).length) {
      lines.push(`\n### Géométrie`);
      lines.push(`| Page | Taille (mm) | TrimBox | BleedBox | Fond perdu |`);
      lines.push(`|---|---|---|---|---|`);
      r.pages.forEach((p) => {
        const box = (b) => (b ? `${b.w_mm}×${b.h_mm}` : '—');
        lines.push(`| ${p.index} | ${p.width_mm ?? '?'}×${p.height_mm ?? '?'} | ${box(p.trim)} | ${box(p.bleed)} | ${p.bleed_mm != null ? p.bleed_mm + ' mm' : '—'} |`);
      });
    }
    const col = r.colors || {};
    lines.push(`\n### Couleurs`);
    lines.push(`- Modèles : ${(col.models || []).join(', ') || '—'}`);
    lines.push(`- Tons directs : ${(col.spots || []).join(', ') || '—'}`);
    if ((col.process_seps || []).length) lines.push(`- Séparations process : ${col.process_seps.join(', ')}`);
    if ((r.ink || []).length) {
      lines.push(`\n### Taux d'encre (TAC)`);
      lines.push(`| Profil | TAC max | Limite | Verdict |`);
      lines.push(`|---|---|---|---|`);
      r.ink.forEach((k) => lines.push(`| ${k.label} | ${k.tac_max ?? '?'}% | ${k.tac_limit ?? '?'}% | ${k.over ? '❌ dépassé' : '✅ OK'} |`));
    }
    if ((r.fonts || []).length) {
      lines.push(`\n### Polices`);
      r.fonts.forEach((f) => lines.push(`- ${f.embedded ? '✅' : '❌'} ${f.name}${f.embedded ? '' : ' (non intégrée)'}`));
    }
    const st = r.standards || {};
    const lay = r.layers || {};
    lines.push(`\n### Normes & calques`);
    lines.push(`- PDF/X : ${st.pdfx || 'non'}`);
    lines.push(`- Transparence : ${st.transparency ? 'oui' : 'non'} · Surimpression : ${st.overprint ? 'oui' : 'non'}`);
    lines.push(`- Calques : ${lay.has_ocg ? ((lay.names || []).join(', ') || 'oui') : 'aucun (PDF aplati)'}`);
  });
  lines.push(`\n_Généré par PolyPrint Studio — ${now.toISOString().slice(0, 16).replace('T', ' ')}_`);
  return lines.join('\n');
}

const CUT_RE = /cut|contour|d[ée]coup|dieline|knife|crease|perfo/i;

function Badge({ severity }) {
  const s = SEV[severity] || SEV.info;
  return (
    <span style={{ background: s.bg, color: s.color, borderColor: s.color }}
      className="inline-flex items-center justify-center w-[1.4em] h-[1.4em] rounded-full border text-[0.62em] font-bold shrink-0 mt-[0.15em]">{s.icon}</span>
  );
}

function CountChip({ n, sev, label }) {
  const s = SEV[sev] || SEV.info;
  const on = n > 0;
  return (
    <span className="text-[0.72em] font-mono font-bold px-[0.6em] py-[0.2em] rounded-full border"
      style={{ color: on ? s.color : 'rgba(var(--forge-text),0.35)', borderColor: on ? s.color : 'rgba(var(--forge-text),0.12)', background: on ? s.bg : 'transparent' }}>
      {n} {label}{n > 1 ? 's' : ''}
    </span>
  );
}

function Chip({ children, tone = 'neutral' }) {
  const m = { rgb: '#f59e0b', neutral: 'rgba(var(--forge-text),0.55)', spot: '#a78bfa', cmjn: '#34d399', cut: '#f472b6' };
  const c = m[tone] || m.neutral;
  return <span style={{ color: c, borderColor: c }} className="text-[0.72em] font-mono px-[0.6em] py-[0.18em] rounded-full border bg-white/[0.03]">{children}</span>;
}

/** Cartouche = zone titrée, encadrée, avec une description de ce qu'elle contient. */
function Cartouche({ title, desc, accent = '#818cf8', children }) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <header className="px-[1em] py-[0.7em] border-b border-white/[0.06]" style={{ backgroundColor: `color-mix(in srgb, ${accent} 8%, transparent)` }}>
        <h4 className="text-[0.9em] font-bold leading-tight uppercase tracking-wide" style={{ color: accent }}>{title}</h4>
        {desc && <p className="text-[0.68em] text-white/40 leading-snug mt-[0.2em]">{desc}</p>}
      </header>
      <div className="px-[1em] py-[0.8em]">{children}</div>
    </section>
  );
}

function CheckRow({ c }) {
  const s = SEV[c.severity] || SEV.info;
  return (
    <div className="flex items-start gap-[0.6em] rounded-lg px-[0.7em] py-[0.5em]" style={{ background: s.bg }}>
      <Badge severity={c.severity} />
      <div className="min-w-0">
        <div className="text-[0.82em] font-bold leading-tight" style={{ color: s.color }}>{c.label}</div>
        <div className="text-[0.8em] text-white/70 leading-snug mt-[0.1em]">{c.message}</div>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-[0.8em] py-[0.1em]">
      <span className="text-white/40 shrink-0">{k}</span>
      <span className="text-white/85 font-mono text-right truncate">{v}</span>
    </div>
  );
}

function ReportCard({ report }) {
  if (report.error) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-[1em] text-[0.92em] text-red-300">
        <span className="font-bold">{report.filename}</span> — {report.error}
      </div>
    );
  }
  const s = report.summary || {};
  const d = report.document || {};
  const col = report.colors || {};
  const st = report.standards || {};
  const lay = report.layers || {};
  const checks = report.checks || [];
  const sevOrder = { error: 0, warning: 1, info: 2, ok: 3 };
  const sortedChecks = [...checks].sort((a, b) => (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9));
  const nOk = checks.filter((c) => c.severity === 'ok').length;
  const layTxt = lay.has_ocg ? ((lay.names || []).join(', ') || 'oui') : 'aucun (PDF aplati)';

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-[1em] flex flex-col gap-[0.8em]">
      {/* En-tête fichier */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-[1.05em] font-bold text-white/95 truncate min-w-0">{report.filename}</h3>
        <div className="flex items-center gap-[0.4em] shrink-0">
          <CountChip n={s.errors || 0} sev="error" label="erreur" />
          <CountChip n={s.warnings || 0} sev="warning" label="alerte" />
          <CountChip n={nOk} sev="ok" label="OK" />
        </div>
      </div>

      {/* Hero : les contrôles (le verdict) */}
      <Cartouche title="Contrôles" desc="Diagnostic prépresse — erreurs, alertes et points conformes" accent="#818cf8">
        <div className="flex flex-col gap-[0.4em]">
          {sortedChecks.map((c, i) => <CheckRow key={i} c={c} />)}
          {!sortedChecks.length && <span className="text-[0.8em] text-white/40">Aucun contrôle.</span>}
        </div>
      </Cartouche>

      <div className="grid sm:grid-cols-2 gap-[0.8em]">
        <Cartouche title="Document" desc="Version, pages et métadonnées" accent="#60a5fa">
          <KV k="Version" v={d.pdf_version || '?'} />
          <KV k="Pages" v={d.page_count ?? '?'} />
          <KV k="Poids" v={`${Math.round((report.file_size || 0) / 1024)} Ko`} />
          {d.producer && <KV k="Producteur" v={d.producer} />}
          {d.title && <KV k="Titre" v={d.title} />}
        </Cartouche>

        <Cartouche title="Couleurs & tons directs" desc="Modèles colorimétriques et tons directs (dont découpe)" accent="#a78bfa">
          <div className="flex flex-wrap gap-[0.4em]">
            {(col.models || []).map((m) => <Chip key={m} tone={m === 'RGB' ? 'rgb' : m === 'CMJN' ? 'cmjn' : 'neutral'}>{m}</Chip>)}
            {(col.spots || []).map((sp) => <Chip key={sp} tone={CUT_RE.test(sp) ? 'cut' : 'spot'}>◆ {sp}</Chip>)}
            {!(col.models || []).length && !(col.spots || []).length && <span className="text-[0.8em] text-white/30">—</span>}
          </div>
          {(col.process_seps || []).length > 0 && (
            <div className="mt-[0.6em] text-[0.7em] text-white/35">Séparations process : {col.process_seps.join(', ')}</div>
          )}
        </Cartouche>
      </div>

      {(report.pages || []).length > 0 && (
        <Cartouche title="Géométrie & fond perdu" desc="Formats et boîtes (Trim/Bleed), fond perdu mesuré bord par bord" accent="#22d3ee">
          <div className="flex flex-col gap-[0.25em] font-mono text-white/70">
            {report.pages.slice(0, 8).map((p) => (
              <div key={p.index} className="text-[0.78em]">
                <span className="text-white/40">p{p.index}</span> · {p.width_mm ?? '?'}×{p.height_mm ?? '?'} mm
                <span className="text-white/40"> · Trim</span> {p.trim ? `${p.trim.w_mm}×${p.trim.h_mm}` : '—'}
                <span className="text-white/40"> · Bleed</span> {p.bleed ? `${p.bleed.w_mm}×${p.bleed.h_mm}` : '—'}
                {p.bleed_mm != null && <span className="text-white/40"> · fond perdu {p.bleed_mm} mm</span>}
              </div>
            ))}
          </div>
        </Cartouche>
      )}

      {(report.ink || []).length > 0 && (
        <Cartouche title="Taux d'encre (TAC)" desc="Couverture d'encre maximale par profil ICC" accent="#34d399">
          <div className="flex flex-col gap-[0.35em]">
            {report.ink.map((k, i) => (
              <div key={i} className="text-[0.8em] font-mono flex items-center gap-[0.6em] flex-wrap">
                <span className="text-white/60">{k.label}</span>
                <span className="font-bold" style={{ color: k.over ? SEV.error.color : SEV.ok.color }}>{k.tac_max ?? '?'}%</span>
                <span className="text-white/35">/ {k.tac_limit ?? '?'}% {k.over ? '✕ dépassé' : '✓'}</span>
                {k.note && <span className="text-white/30 italic">({k.note})</span>}
              </div>
            ))}
          </div>
        </Cartouche>
      )}

      {(report.fonts || []).length > 0 && (
        <Cartouche title="Polices" desc="Intégration des polices (risque de substitution si non intégrée)" accent="#fbbf24">
          <div className="flex flex-wrap gap-[0.4em]">
            {report.fonts.slice(0, 16).map((f, i) => (
              <span key={i} className="text-[0.72em] font-mono px-[0.6em] py-[0.15em] rounded-full border"
                style={{ color: f.embedded ? 'rgba(var(--forge-text),0.65)' : SEV.error.color, borderColor: f.embedded ? 'rgba(var(--forge-text),0.15)' : SEV.error.color }}>
                {f.embedded ? '' : '⚠ '}{f.name}
              </span>
            ))}
          </div>
        </Cartouche>
      )}

      <Cartouche title="Normes, calques & rendu" desc="Conformité PDF/X, calques (découpe), transparence et surimpression" accent="#f472b6">
        <div className="grid grid-cols-2 gap-x-3">
          <KV k="PDF/X" v={st.pdfx || 'non'} />
          <KV k="Transparence" v={st.transparency ? 'oui' : 'non'} />
          <KV k="Surimpression" v={st.overprint ? 'oui' : 'non'} />
          <KV k="Images" v={(report.images || []).length || '—'} />
          <KV k="Calques" v={layTxt} />
        </div>
      </Cartouche>
    </div>
  );
}

/** Aperçu du PDF dans un cadre à HAUTEUR FIXE : le zoom agrandit l'image DANS le
 *  cadre (sans changer la taille de la modale) et on se déplace par glisser
 *  (Espace maintenu, ou directement quand on est zoomé) ou via les flèches. */
function PdfPreview({ report, showCut, showImages, zoom, viewportRef }) {
  const pages = report.preview || [];
  const regions = report.regions || [];
  const drag = useRef(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [grabbing, setGrabbing] = useState(false);

  useEffect(() => {
    const dn = (e) => {
      if (e.code !== 'Space') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      setSpaceHeld(true);
      e.preventDefault(); // évite le scroll de page
    };
    const up = (e) => { if (e.code === 'Space') setSpaceHeld(false); };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
  }, []);

  if (!pages.length) return null;
  const regionColor = (r) => ((r.kind === 'cut' || r.kind === 'cut_fill') ? '#f472b6' : (SEV[r.severity]?.color || '#f59e0b'));
  const canPan = spaceHeld || zoom > 1;

  const onDown = (e) => {
    if (!canPan) return;
    const vp = viewportRef.current; if (!vp) return;
    drag.current = { x: e.clientX, y: e.clientY, sl: vp.scrollLeft, st: vp.scrollTop };
    setGrabbing(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const vp = viewportRef.current; if (!vp) return;
    vp.scrollLeft = drag.current.sl - (e.clientX - drag.current.x);
    vp.scrollTop = drag.current.st - (e.clientY - drag.current.y);
  };
  const onUp = () => { drag.current = null; setGrabbing(false); };

  return (
    <div ref={viewportRef}
      className="overflow-auto custom-scrollbar rounded-xl border border-white/10 bg-black/40 p-2"
      style={{ height: 'clamp(200px, 38vh, 440px)', cursor: canPan ? (grabbing ? 'grabbing' : 'grab') : 'default', touchAction: canPan ? 'none' : 'auto' }}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
      <div className="flex flex-col gap-3 mx-auto" style={{ width: `${Math.round(zoom * 100)}%` }}>
        {pages.map((pg) => {
          const pageRegions = regions.filter((r) => r.page === pg.index && (r.kind === 'image_lowres' ? showImages : showCut));
          return (
            <div key={pg.index} className="relative" style={{ lineHeight: 0 }}>
              <img src={`data:${pg.mime};base64,${pg.b64}`} alt={`page ${pg.index}`} draggable={false}
                className="w-full block rounded" style={{ aspectRatio: `${pg.w} / ${pg.h}` }} />
              {pageRegions.map((r, i) => {
                const col = regionColor(r);
                const strong = r.kind === 'cut_fill';
                return (
                  <div key={i} title={r.label} className="absolute pointer-events-none rounded-[2px]"
                    style={{
                      left: `${r.frac[0] * 100}%`, top: `${r.frac[1] * 100}%`,
                      width: `${(r.frac[2] - r.frac[0]) * 100}%`, height: `${(r.frac[3] - r.frac[1]) * 100}%`,
                      border: `${strong ? 2 : 1.5}px solid ${col}`, background: `${col}${strong ? '33' : '22'}`,
                    }} />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PdfPreflightModal({ isOpen, onClose }) {
  const [files, setFiles] = useState([]);          // File[]
  const [profiles, setProfiles] = useState([]);    // [{id,label,tac}]
  const [selected, setSelected] = useState([]);    // profile ids
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');   // « Envoi 2/3… 45 % », « Analyse… »
  const [reports, setReports] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fontScale, setFontScale] = useState(1); // taille du texte du rapport
  const [activeIdx, setActiveIdx] = useState(0);          // fichier affiché à droite
  const [selectedIdxs, setSelectedIdxs] = useState(new Set()); // fichiers cochés (rapport global)
  const [showCut, setShowCut] = useState(true);       // surligner les zones de découpe
  const [showImages, setShowImages] = useState(true); // surligner les images basse réso
  const [previewZoom, setPreviewZoom] = useState(1);
  const inputRef = useRef(null);
  const previewRef = useRef(null); // cadre scrollable de l'aperçu (pour le pan)
  const pan = (dx, dy) => previewRef.current?.scrollBy({ left: dx, top: dy, behavior: 'smooth' });

  const stepFont = (dir) => setFontScale((cur) => {
    const i = FONT_SCALES.indexOf(cur);
    const ni = Math.min(FONT_SCALES.length - 1, Math.max(0, (i < 0 ? 1 : i) + dir));
    return FONT_SCALES[ni];
  });

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    (async () => {
      try {
        const r = await authedFetch('/api/export/print-capabilities');
        if (!r.ok) return;
        const data = await r.json();
        const list = Object.entries(data.profiles || {}).map(([id, p]) => ({ id, label: p.label, tac: p.tac }));
        setProfiles(list);
        setSelected(list.map((p) => p.id)); // tout sélectionné par défaut
      } catch { /* profils indisponibles : TAC sauté */ }
    })();
  }, [isOpen]);

  if (!isOpen) return null;

  const addFiles = (fileList) => {
    const pdfs = Array.from(fileList || []).filter((f) => f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)));
    if (pdfs.length) { setFiles((prev) => [...prev, ...pdfs].slice(0, 10)); setReports(null); }
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const toggleProfile = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);

  const analyze = async () => {
    if (!files.length || loading) return;
    setLoading(true); setError(null); setReports(null);
    try {
      // UNE requête par fichier : la limite de 32 Mio de Cloud Run vaut pour la
      // requête entière, et trois PDF de 15 Mo la dépassaient ensemble. Un fichier
      // qui échoue rend sa propre ligne d'erreur sans annuler les autres.
      const reps = [];
      for (const [i, f] of files.entries()) {
        const etape = files.length > 1 ? ` ${i + 1}/${files.length}` : '';
        try {
          const fd = new FormData();
          fd.append('profiles', selected.join(','));
          if (voieDEnvoi(f) === 's3') {
            setProgress(`Envoi${etape}… 0 %`);
            const key = await deposerSurS3(f, (ratio) => setProgress(`Envoi${etape}… ${Math.round(ratio * 100)} %`));
            fd.append('s3_key', key);
            fd.append('filename', f.name);
          } else {
            fd.append('files', f, f.name);
          }
          setProgress(`Analyse${etape}…`);
          const r = await authedFetch('/api/export/preflight', { method: 'POST', body: fd });
          if (!r.ok) throw new Error(await detailDErreur(r));
          const data = await r.json();
          reps.push(...(data.reports || []));
        } catch (e) {
          reps.push({ filename: f.name, error: e.message || 'Analyse impossible' });
        }
      }
      setReports(reps);
      setActiveIdx(0);
      setSelectedIdxs(new Set(reps.map((_, i) => i))); // tout coché par défaut
    } catch (e) {
      setError(e.message || 'Analyse impossible');
    } finally {
      setLoading(false); setProgress('');
    }
  };

  const download = (list, prefix) => {
    if (!list || !list.length) return;
    const blob = new Blob([buildMarkdown(list)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${prefix}_${Date.now()}.md`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const worst = (rep) => (rep.error || (rep.summary?.errors > 0)) ? 'error' : (rep.summary?.warnings > 0 ? 'warning' : 'ok');
  const toggleSel = (i) => setSelectedIdxs((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const allSel = reports && reports.length > 0 && selectedIdxs.size === reports.length;
  const totalErr = (reports || []).reduce((a, r) => a + (r.error ? 1 : (r.summary?.errors || 0)), 0);
  const totalWarn = (reports || []).reduce((a, r) => a + (r.summary?.warnings || 0), 0);
  const active = reports ? reports[activeIdx] : null;
  const fsBtn = "w-7 h-7 rounded-lg border border-white/15 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center font-bold";

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center modal-backdrop backdrop-blur-md p-4">
      <div className="bg-forge-bg/95 backdrop-blur-md border border-forge-border/20 rounded-2xl flex flex-col relative shadow-2xl"
        style={{ width: 'min(1040px, 94vw)', height: 'min(820px, 90vh)', minWidth: '520px', minHeight: '380px', maxWidth: '96vw', maxHeight: '94vh', resize: 'both', overflow: 'hidden' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><circle cx="11" cy="14" r="2.4" /><line x1="12.8" y1="15.8" x2="15" y2="18" />
            </svg>
            <h2 className="text-[14px] font-mono font-bold text-white/90 uppercase tracking-wider">Analyse PDF — préflight</h2>
          </div>
          <div className="flex items-center gap-2">
            {reports && (
              <div className="flex items-center gap-1.5 mr-1">
                <span className="text-[10px] font-mono text-white/40">Texte</span>
                <button onClick={() => stepFont(-1)} disabled={fontScale <= FONT_SCALES[0]} title="Réduire le texte" aria-label="Réduire le texte" className={`${fsBtn} text-[11px]`}>A−</button>
                <button onClick={() => stepFont(1)} disabled={fontScale >= FONT_SCALES[FONT_SCALES.length - 1]} title="Agrandir le texte" aria-label="Agrandir le texte" className={`${fsBtn} text-[14px]`}>A+</button>
              </div>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/[0.06]">✕</button>
          </div>
        </div>

        {!reports ? (
          /* ───────── ÉTAPE 1 : chargement des fichiers ───────── */
          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4 custom-scrollbar">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); }}
              onClick={() => inputRef.current?.click()}
              className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/15 hover:border-white/30 bg-black/20'}`}>
              <p className="text-[12px] font-mono text-white/60">Glisse 1 ou plusieurs PDF ici, ou clique pour choisir</p>
              <p className="text-[10px] font-mono text-white/30 mt-1">10 fichiers max · 80 Mo / fichier</p>
              <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>

            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {files.map((f, i) => (
                  <span key={i} className="text-[10px] font-mono px-2 py-1 rounded-lg border border-white/10 bg-white/[0.03] text-white/70 flex items-center gap-1.5">
                    {f.name}
                    <button onClick={() => removeFile(i)} className="text-white/40 hover:text-red-400">✕</button>
                  </span>
                ))}
              </div>
            )}

            {profiles.length > 0 && (
              <div>
                <span className="text-[9px] font-mono uppercase tracking-wider text-white/40">Profil(s) ICC pour le taux d'encre (TAC)</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {profiles.map((p) => (
                    <button key={p.id} onClick={() => toggleProfile(p.id)}
                      className={`text-[10px] font-mono px-2.5 py-1.5 rounded-lg border transition-all ${selected.includes(p.id)
                        ? 'bg-indigo-600 border-indigo-400/60 text-indigo-50'
                        : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:text-white/80'}`}>
                      {p.label} ({p.tac}%)
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button onClick={analyze} disabled={!files.length || loading}
              className="w-full py-2.5 rounded-xl text-[11px] font-mono font-bold uppercase tracking-wider border transition-all bg-indigo-600 border-indigo-400/50 text-indigo-50 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed">
              {loading ? (progress || 'Analyse en cours…') : `Analyser ${files.length || ''} fichier(s)`}
            </button>

            {error && <div className="text-[11px] font-mono text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">⚠ {error}</div>}
          </div>
        ) : (
          /* ───────── ÉTAPE 2 : résultats (2 colonnes) ───────── */
          <div className="flex-1 flex min-h-0">
            {/* Colonne gauche : liste des fichiers */}
            <aside className="w-[240px] shrink-0 border-r border-white/[0.06] flex flex-col min-h-0">
              <div className="px-3 py-2.5 border-b border-white/[0.05] font-mono">
                <div className="text-[10px] text-white/50">{reports.length} fichier{reports.length > 1 ? 's' : ''} analysé{reports.length > 1 ? 's' : ''}</div>
                <div className="flex gap-3 mt-1 text-[10px] font-bold">
                  <span style={{ color: totalErr ? SEV.error.color : 'rgba(var(--forge-text),0.35)' }}>{totalErr} erreur{totalErr > 1 ? 's' : ''}</span>
                  <span style={{ color: totalWarn ? SEV.warning.color : 'rgba(var(--forge-text),0.35)' }}>{totalWarn} alerte{totalWarn > 1 ? 's' : ''}</span>
                </div>
              </div>
              <label className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.05] cursor-pointer text-[11px] font-mono text-white/55 hover:text-white/90">
                <input type="checkbox" checked={allSel} onChange={() => setSelectedIdxs(allSel ? new Set() : new Set(reports.map((_, i) => i)))} />
                Tout sélectionner
              </label>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {reports.map((rep, i) => {
                  const c = SEV[worst(rep)].color;
                  return (
                    <div key={i} onClick={() => { setActiveIdx(i); setPreviewZoom(1); }}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-l-2 transition-colors ${i === activeIdx ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'}`}
                      style={{ borderLeftColor: i === activeIdx ? '#818cf8' : 'transparent' }}>
                      <input type="checkbox" checked={selectedIdxs.has(i)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(i)} />
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c }} />
                      <span className="text-[11px] font-mono text-white/70 truncate flex-1" title={rep.filename}>{rep.filename}</span>
                    </div>
                  );
                })}
              </div>
              <div className="p-2.5 border-t border-white/[0.06] flex flex-col gap-1.5">
                <button onClick={() => download(reports.filter((_, i) => selectedIdxs.has(i)), 'preflight_global')} disabled={!selectedIdxs.size}
                  className="text-[10px] font-mono uppercase tracking-wider px-3 py-2 rounded-lg border bg-indigo-600 border-indigo-400/40 text-indigo-50 hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                  ↓ Rapport global ({selectedIdxs.size})
                </button>
                <button onClick={() => setReports(null)}
                  className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-white/15 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/10 transition-all">
                  ↻ Changer les fichiers
                </button>
              </div>
            </aside>

            {/* Colonne droite : aperçu + rapport du fichier actif */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 custom-scrollbar min-w-0">
              {active && active.error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-[12px] text-red-400">
                  <span className="font-bold">{active.filename}</span> — {active.error}
                </div>
              )}

              {active && !active.error && (active.preview || []).length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/40">Aperçu</span>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {(active.regions || []).some((r) => r.kind === 'cut_fill' || r.kind === 'cut') && (
                        <button onClick={() => setShowCut((v) => !v)} title="Afficher/masquer les zones de couleur de découpe"
                          className={`text-[10px] font-mono px-2 py-1 rounded-lg border transition-all ${showCut ? '' : 'border-white/15 bg-white/[0.04] text-white/50 hover:text-white/80'}`}
                          style={showCut ? { borderColor: '#f472b6', color: '#f472b6', background: '#f472b61f' } : undefined}>
                          Découpe
                        </button>
                      )}
                      {(active.regions || []).some((r) => r.kind === 'image_lowres') && (
                        <button onClick={() => setShowImages((v) => !v)} title="Afficher/masquer les images basse résolution"
                          className={`text-[10px] font-mono px-2 py-1 rounded-lg border transition-all ${showImages ? '' : 'border-white/15 bg-white/[0.04] text-white/50 hover:text-white/80'}`}
                          style={showImages ? { borderColor: '#f59e0b', color: '#f59e0b', background: '#f59e0b1f' } : undefined}>
                          Images
                        </button>
                      )}
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => setPreviewZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))} disabled={previewZoom <= 1} title="Dézoomer" aria-label="Dézoomer" className={`${fsBtn} text-[14px]`}>−</button>
                        <span className="text-[10px] font-mono text-white/40 w-9 text-center">{Math.round(previewZoom * 100)}%</span>
                        <button onClick={() => setPreviewZoom((z) => Math.min(4, +(z + 0.5).toFixed(1)))} disabled={previewZoom >= 4} title="Zoomer" aria-label="Zoomer" className={`${fsBtn} text-[14px]`}>+</button>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => pan(-140, 0)} title="Gauche" aria-label="Gauche" className={`${fsBtn} text-[12px]`}>←</button>
                        <button onClick={() => pan(0, -120)} title="Haut" aria-label="Haut" className={`${fsBtn} text-[12px]`}>↑</button>
                        <button onClick={() => pan(0, 120)} title="Bas" aria-label="Bas" className={`${fsBtn} text-[12px]`}>↓</button>
                        <button onClick={() => pan(140, 0)} title="Droite" aria-label="Droite" className={`${fsBtn} text-[12px]`}>→</button>
                      </div>
                    </div>
                  </div>
                  <PdfPreview report={active} showCut={showCut} showImages={showImages} zoom={previewZoom} viewportRef={previewRef} />
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] font-mono text-white/40">
                    <span className="text-white/35">Glisser (ou Espace + glisser) ou flèches pour se déplacer.</span>
                    {showImages && (active.regions || []).some((r) => r.kind === 'image_lowres') && (
                      <span><span className="inline-block w-2.5 h-2.5 rounded-[2px] mr-1 align-middle" style={{ background: '#f59e0b88', border: '1px solid #f59e0b' }} />image basse réso (précis)</span>
                    )}
                    {showCut && (active.regions || []).some((r) => r.kind === 'cut_fill') && (
                      <span><span className="inline-block w-2.5 h-2.5 rounded-[2px] mr-1 align-middle" style={{ background: '#f472b6aa', border: '2px solid #f472b6' }} />couleur de découpe (zone précise)</span>
                    )}
                    {showCut && (active.regions || []).some((r) => r.kind === 'cut') && (
                      <span><span className="inline-block w-2.5 h-2.5 rounded-[2px] mr-1 align-middle" style={{ background: '#f472b688', border: '1px solid #f472b6' }} />couleur de découpe (approx.)</span>
                    )}
                  </div>
                </div>
              )}

              {active && (
                <div className="flex items-center justify-end">
                  <button onClick={() => download([active], 'preflight')}
                    className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-white/15 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/10 transition-all">
                    ↓ Ce rapport (.md)
                  </button>
                </div>
              )}

              <div style={{ fontSize: `${Math.round(13 * fontScale)}px` }}>
                {active && <ReportCard report={active} />}
              </div>
            </div>
          </div>
        )}
        <div className="absolute bottom-0.5 right-0.5 pointer-events-none select-none text-white/30 text-[11px] leading-none" title="Redimensionner">⤡</div>
      </div>
    </div>
  );
}
